import { getStriveBaseUrl, striveHeaders } from "./striveApi";

const STORAGE_KEY_PAYLOAD = "categoryMetadata:payload";
const STORAGE_KEY_LAST_MODIFIED = "categoryMetadata:lastModified";

interface CategoryItem {
  id: number;
  name: string;
  synonyms: { id: number; name: string }[];
  active: boolean;
  rune_classification: string;
  tags: { display_name: string; name: string }[];
}

interface ChildCategory {
  id: number;
  name: string;
  active: boolean;
  uuid: string;
  items: CategoryItem[];
}

interface TopLevelCategory {
  id: number;
  name: string;
  active: boolean;
  uuid: string;
  child_categories: ChildCategory[];
}

export interface MedicationOption {
  name: string;
  classification: string;
}

export interface MedicationTagGroup {
  tagName: string;
  displayName: string;
  /** Medication enum values belonging to this group */
  medicationEnums: string[];
}

export async function getCategoryMetadata(): Promise<TopLevelCategory[]> {
  const cachedLastModified = localStorage.getItem(STORAGE_KEY_LAST_MODIFIED);
  const cachedPayload = localStorage.getItem(STORAGE_KEY_PAYLOAD);

  const baseUrl = getStriveBaseUrl();
  const headers = await striveHeaders();
  if (cachedLastModified) {
    headers["If-Modified-Since"] = cachedLastModified;
  }

  const response = await fetch(`${baseUrl}/api/v3/category_metadata`, {
    headers
  });

  // 304 Not Modified — use cache
  if (response.status === 304) {
    if (cachedPayload) {
      return JSON.parse(cachedPayload);
    }
    return [];
  }

  if (!response.ok) {
    if (cachedPayload) {
      return JSON.parse(cachedPayload);
    }
    throw new Error(`Failed to fetch category metadata: ${response.status}`);
  }

  const data = await response.json();

  // Store new data
  const lastModified = response.headers.get("Last-Modified");
  if (lastModified) {
    localStorage.setItem(STORAGE_KEY_LAST_MODIFIED, lastModified);
  }
  localStorage.setItem(STORAGE_KEY_PAYLOAD, JSON.stringify(data.payload));

  return data.payload;
}

export function extractMedicationOptions(
  categories: TopLevelCategory[]
): MedicationOption[] {
  const medsCategory = categories.find(
    (c) => c.uuid === "medications_&_supplements"
  );
  if (!medsCategory) return [];

  const medicationChild = medsCategory.child_categories.find(
    (c) => c.name === "Medication"
  );
  if (!medicationChild) return [];

  return medicationChild.items
    .filter((item) => item.active)
    .map((item) => {
      // rune_classification is dotted like "patient.medication.rytary"
      // Extract the enum portion (last segment) to match event classification.enum
      const parts = item.rune_classification.split(".");
      const enumValue = parts[parts.length - 1];
      return {
        name: item.name,
        classification: enumValue
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function extractMedicationTagGroups(
  categories: TopLevelCategory[]
): MedicationTagGroup[] {
  const medsCategory = categories.find(
    (c) => c.uuid === "medications_&_supplements"
  );
  if (!medsCategory) return [];

  const medicationChild = medsCategory.child_categories.find(
    (c) => c.name === "Medication"
  );
  if (!medicationChild) return [];

  const groupMap = new Map<string, MedicationTagGroup>();

  for (const item of medicationChild.items) {
    if (!item.active) continue;
    const parts = item.rune_classification.split(".");
    const enumValue = parts[parts.length - 1];

    for (const tag of item.tags) {
      if (!tag.name.startsWith("medication.")) continue;
      const existing = groupMap.get(tag.name);
      if (existing) {
        existing.medicationEnums.push(enumValue);
      } else {
        groupMap.set(tag.name, {
          tagName: tag.name,
          displayName: tag.display_name,
          medicationEnums: [enumValue]
        });
      }
    }
  }

  return Array.from(groupMap.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );
}
